import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import dayjs from "dayjs";
// eslint-disable-next-line import/no-extraneous-dependencies
import { fromEvent } from "file-selector";
import { chain, capitalize, isEmpty, compact } from "lodash";
import moment from "moment";
import { useDropzone } from "react-dropzone";
import { queryCache } from "react-query";

import { useGetDeal } from "manager/hooks/api";
import { useLegalAuditDocumentRedaction } from "manager/hooks/useLegalAuditDocumentRedaction";
import { useGetOcrJobs } from "renter/hooks/api";
import { openMessenger } from "shared/components/Messenger/messengerUtils";
import {
  UPLOAD_ALLOWED_FILE_TYPES,
  MAX_FILE_UPLOAD_SIZE,
  NOTIFICATIONS,
  COLORS,
  OCR_API_DELAY,
  OCR_API_MAX_RETRIES,
  OCR_MAX_WAITING_TIME_MINUTES,
} from "shared/config/constants";
import { useActiveUser } from "shared/hooks/api";
import { GET_DOCUMENT_DETAIL } from "shared/hooks/api/applicationQueries";
import useModal from "shared/hooks/useModal";
import type {
  Document,
  DocumentInApplication,
} from "shared/interfaces/api/document";
import ApplicationContext from "shared/pages/ApplicationSummary/applicationContext";
import { sleep } from "shared/utils/async";
import { isManagerApp, isRenterApp } from "shared/utils/auth";
import { differenceBetweenDatesInMinutes } from "shared/utils/dates";
import { useVeroFlag } from "shared/utils/featureFlags";
import { openNotification } from "shared/utils/ui";
import { VeroFlag } from "shared/utils/veroFlags";

import { LoadingSpinnerIcon } from "../Button/Button/styled";
import FlexContainer from "../FlexContainer";
import Icon from "../Icon";
import Spacer from "../Spacer";
import Tooltip from "../Tooltip";
import { MediumTitle, RegularText } from "../Typography";

import { DeleteDocumentModal } from "./DeleteDocumentModal";
import { DocumentLink } from "./DocumentLink";
import { DocumentsUploadModal } from "./DocumentsUploadModal";

import { LumenReady } from "./LumenReady";
import {
  DeleteDocumentIcon,
  DocumentTable,
  DocumentTableHeader,
  UploadIconContainer,
  UploadZone,
} from "./styled";
import {
  isFileEncrypted,
  isFileFormatValid,
  isFileSizeValid,
  isFileContentValid,
} from "./utils";

const isDocument = (document: any): document is Document =>
  typeof document.createdAt !== "undefined" &&
  typeof document.documentName !== "undefined" &&
  typeof document.description !== "undefined" &&
  typeof document.id !== "undefined";

const INTERNATIONAL_FRAUD_RISK_TYPES = [
  "international-paystub",
  "international-tax-form",
  "international-letter-of-employment",
];

// Manager international uploads send no type, so document-service leaves tax
// forms and employment letters untyped; the modal's "<Type> (<Country>)"
// description is the only record of what was picked.
const INTERNATIONAL_FRAUD_RISK_DESCRIPTION_PREFIXES = [
  "paystub (",
  "tax form (",
  "employment letter (",
];

const isInternationalFraudRiskDocument = ({
  type,
  documentIntent,
  description,
}: Partial<Document>) => {
  if (INTERNATIONAL_FRAUD_RISK_TYPES.includes(type ?? "")) return true;
  if (type || documentIntent?.toLowerCase() !== "international") return false;
  const loweredDescription = description?.toLowerCase() ?? "";
  return INTERNATIONAL_FRAUD_RISK_DESCRIPTION_PREFIXES.some((prefix) =>
    loweredDescription.startsWith(prefix)
  );
};

const getCurrentResidenceCountry = (application) =>
  application?.residentialHistories?.find(({ livesHere }) => livesHere)?.country ??
  application?.residentialHistories?.find(({ moveOutDate }) => !moveOutDate)
    ?.country ??
  application?.residentialHistories?.[0]?.country;

const AttachmentsRedesigned = () => {
  const { openModalDialog } = useModal();
  const {
    application,
    application: { snapshot },
  } = useContext(ApplicationContext);
  const { data: deal } = useGetDeal(application?.deal, {
    enabled: !isEmpty(application?.deal),
  });
  const { activeUser } = useActiveUser();
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const intlOcrFixesEnabled = useVeroFlag(VeroFlag.useIntlOcrFixes);
  const showInternationalFraudRisk =
    intlOcrFixesEnabled &&
    application?.internationalIncomePolicy === "include";
  const { ocrJobs, refetchOcrJobs, isOcrJobsFetching } = useGetOcrJobs(
    application?.personVrn,
    !isEmpty(application?.personVrn)
  );
  const originalDocumentsRef = useRef<Document[]>([]);
  // 0: success/failed, 1: in-progress, 2: OCR wasn't confirmed even after 15 minutes
  const [inProgressJobs, setInProgressJobs] = useState<{
    [key: string]: number;
  }>({});

  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useState<
    DocumentInApplication[]
  >([]);
  const [ocrRetries, setOcrRetries] = useState(1);
  const defaultDocumentCountry = getCurrentResidenceCountry(application);
  const requestedDocumentTypes = requiredDocuments.map(({ name }) => name);

  useEffect(() => {
    originalDocumentsRef.current = snapshot?.originalDocuments ?? [];
  }, []);

  const getDocuments = useCallback(() => {
    const requiredDocuments: (
      | Document
      | (DocumentInApplication & { index: number })
    )[] = chain(application)
      .pick([
        "financialDocuments",
        "requiredDocuments",
        "unverifiedIncomeDocuments",
        "unverifiedBackgroundDocuments",
      ])
      .flatMap()
      .filter(({ isRequired }) => isRequired)
      .uniqBy("name")
      .sortBy([({ name }) => (name as string).toLowerCase()])
      .value();

    const uploadedDocuments = chain(snapshot)
      .pick(
        compact([isManagerApp() && "applicantDocuments", "originalDocuments"])
      )
      .values()
      .flatMap()
      .filter(({ tags, type, description }) => {
        const isRestricted = tags?.includes("restricted");
        const isSummaryWithEmptyDescription =
          type === "summary" && (!description || description.trim() === "");

        return (
          !isRestricted ||
          (isRestricted &&
            deal?.canViewRestrictedAttachments &&
            !isSummaryWithEmptyDescription)
        );
      })
      .sortBy([({ description }) => description.toLowerCase()])
      .value();

    const documents = chain(requiredDocuments as DocumentInApplication[])
      .reduce<(Document | (DocumentInApplication & { index: number }))[]>(
        (acc, curr) => {
          for (let i = curr.documents.length; i < curr.requiredQty; i++) {
            acc.push({ ...curr, index: i });
          }
          return acc;
        },
        [...uploadedDocuments]
      )
      .value();
    setRequiredDocuments(requiredDocuments as DocumentInApplication[]);
    setUploadedDocuments(uploadedDocuments as Document[]);
    setDocuments(documents as Document[]);
  }, [application, deal, snapshot]);

  useEffect(() => {
    if (
      originalDocumentsRef?.current?.length !==
      snapshot?.originalDocuments?.length
    ) {
      if (
        originalDocumentsRef?.current?.length <
        snapshot?.originalDocuments?.length
      ) {
        const newInProgressJobs = {};
        snapshot?.originalDocuments?.forEach((doc) => {
          if (!originalDocumentsRef?.current?.some((d) => d?.id === doc?.id)) {
            newInProgressJobs[doc?.id] = 1;
          }
        });
        setInProgressJobs((prev) => ({ ...prev, ...newInProgressJobs }));
        sleep(OCR_API_DELAY).then(() => refetchOcrJobs());
        setOcrRetries(1);
      }
      originalDocumentsRef.current = snapshot?.originalDocuments;
    }
    getDocuments();
  }, [snapshot?.originalDocuments]);

  useEffect(() => {
    if (isOcrJobsFetching) return;
    if (
      ocrJobs?.some((job) => job.status === "in-progress") &&
      ocrRetries < OCR_API_MAX_RETRIES
    ) {
      sleep(OCR_API_DELAY * (ocrRetries * 2)).then(() => refetchOcrJobs());
      setOcrRetries((prev) => prev + 1);
      const newInProgressJobs = {};
      snapshot?.originalDocuments?.forEach((doc) => {
        // Use doc.id to match OCR job's documentId format (both have VRN prefix)
        const findOcrJob = ocrJobs?.find(
          (job) => job.documentId === doc.id && job.status === "in-progress"
        );
        if (findOcrJob) {
          const diffMinutes = differenceBetweenDatesInMinutes(
            findOcrJob?.createdAt,
            dayjs()
          );
          if (diffMinutes >= OCR_MAX_WAITING_TIME_MINUTES) {
            newInProgressJobs[doc?.id] = 2;
          } else {
            newInProgressJobs[doc?.id] = 1;
          }
        } else {
          newInProgressJobs[doc?.id] = 0;
          // Invalidate document detail cache if it was previously in-progress
          if (inProgressJobs[doc?.id] === 1) {
            queryCache.invalidateQueries([GET_DOCUMENT_DETAIL, doc.id]);
          }
        }
      });
      setInProgressJobs((prev) => ({ ...prev, ...newInProgressJobs }));
      getDocuments();
    } else {
      // All jobs complete - invalidate queries for any in-progress documents
      Object.entries(inProgressJobs).forEach(([docId, status]) => {
        if (status === 1) {
          queryCache.invalidateQueries([GET_DOCUMENT_DETAIL, docId]);
        }
      });
      setInProgressJobs({});
      getDocuments();
    }
  }, [isOcrJobsFetching]);

  const validator = (
    file: File & { isContentTypeValid?: boolean; isEncrypted?: boolean }
  ) => {
    const { isContentTypeValid, isEncrypted, name, size } = file;
    const valid =
      isFileFormatValid(name, UPLOAD_ALLOWED_FILE_TYPES) &&
      isFileSizeValid(MAX_FILE_UPLOAD_SIZE, size, name) &&
      !isEncrypted &&
      isContentTypeValid;

    if (isEncrypted) {
      openNotification(
        `${name} is encrypted and cannot be uploaded. Remove the password security protection and try again.`,
        NOTIFICATIONS.error
      );
    }

    if (!valid) return { code: "", message: "" };
    return null;
  };

  // Applicants must not upload from renter portal attachments (TI-2395).
  // Upload belongs in applicant-flow, which sends companySlug/propertyName for OCR routing.
  // Manager portal only; matches legacy Attachments inEditMode={!isRenter}.
  const showUploadZone =
    !isRenterApp() &&
    !application?.dealDecisionIsMade &&
    !shouldRedactDocuments;

  const { getInputProps, getRootProps } = useDropzone({
    disabled: !showUploadZone,
    noClick: !showUploadZone,
    noDrag: !showUploadZone,
    getFilesFromEvent: async (event) => {
      const files = await fromEvent(event);
      return Promise.all(
        files.map(async (file) =>
          Object.defineProperties(file, {
            isContentTypeValid: {
              value: await isFileContentValid(file, UPLOAD_ALLOWED_FILE_TYPES),
            },
            isEncrypted: {
              value: await isFileEncrypted(file),
            },
          })
        )
      );
    },
    onDropAccepted: (acceptedFiles) => {
      openModalDialog(DocumentsUploadModal, {
        acceptedFiles,
        applicationId: application?.id ?? "",
        applicantVrn: application?.applicantVrn ?? "",
        personVrn: application?.personVrn ?? "",
        requiredDocuments,
        propertyName: deal?.property?.name,
      });
    },
    validator,
  });

  return (
    <>
      {application?.dealDecisionIsMade ? (
        <FlexContainer
          alignItems="center"
          flexDirection="row"
          justifyContent="center"
          gap="8px"
        >
          <Icon.InfoBlueIcon height={32} width={32} />
          <MediumTitle>
            The page is now view only. A decision on the deal was already made.
          </MediumTitle>
        </FlexContainer>
      ) : (
        showUploadZone && (
          <UploadZone
            alignItems="center"
            flexDirection="column"
            gap="4px"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <UploadIconContainer
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <Icon.UploadNewIcon height={28} width={28} />
            </UploadIconContainer>
            <RegularText
              underline
              style={{
                color: COLORS.violetTheme.lightViolet2,
                fontSize: "17px",
              }}
            >
              Tap or click to upload
            </RegularText>
            <Spacer size={Spacer.SIZES.xs} />
            <RegularText extraStrong style={{ fontSize: "10px" }}>
              PNG, JPEG, PDF (Max: 5 MB)
            </RegularText>
            <RegularText
              style={{ fontSize: "10px", textAlign: "center", width: "258px" }}
            >
              Avoid combined pdfs. Each paystub or document should be uploaded
              individually.
            </RegularText>
          </UploadZone>
        )
      )}
      <Spacer size={Spacer.SIZES.default} />
      <DocumentTable
        columns={[
          {
            dataIndex: "filename",
            render: (_, { id, documentName, name }) =>
              name ? (
                <RegularText italic light>
                  {capitalize(name)}
                </RegularText>
              ) : (
                <DocumentLink id={id} documentName={documentName} />
              ),
            sorter: (a, b) => {
              if (!isDocument(a) || !isDocument(b)) return 0;
              return a.documentName.localeCompare(b.documentName);
            },
            title: <DocumentTableHeader strong>File Name</DocumentTableHeader>,
          },
          {
            dataIndex: "description",
            render: (_, { description }) =>
              description && (
                <RegularText>
                  {chain(description).replace(/_/g, " ").startCase().value()}
                </RegularText>
              ),
            sorter: (a, b) => {
              if (!isDocument(a) || !isDocument(b)) return 0;
              return a.description.localeCompare(b.description);
            },
            title: (
              <DocumentTableHeader strong>
                User Indicated Type
              </DocumentTableHeader>
            ),
          },
          ...(isManagerApp &&
          deal?.canViewOcrResults &&
          !shouldRedactDocuments
            ? [
                {
                  dataIndex: "lumenReady",
                  render: (_, { id, type, documentIntent, description }) => {
                    const canCopyAnalysisId = !!(
                      activeUser?.isInternal && deal?.canViewOcrResults
                    );
                    if (id || type) {
                      return [
                        "w2",
                        "1099",
                        "paystub",
                        "bank-statement",
                      ].includes(type) ||
                        (showInternationalFraudRisk &&
                          isInternationalFraudRiskDocument({
                            type,
                            documentIntent,
                            description,
                          })) ? (
                        <LumenReady
                          applicationId={application?.id}
                          id={id}
                          isProcessing={inProgressJobs[id] === 1}
                          canCopyAnalysisId={canCopyAnalysisId}
                        />
                      ) : (
                        <RegularText>Unsupported</RegularText>
                      );
                    }
                    return null;
                  },
                  title: (
                    <FlexContainer
                      alignItems="center"
                      flexDirection="row"
                      gap="4px"
                    >
                      <DocumentTableHeader strong>
                        {deal?.lumenEnabled ? "Lumen Ready" : "Fraud Risk"}
                      </DocumentTableHeader>
                      <Icon.BlueQuestionMark
                        height={14}
                        cursor="pointer"
                        width={14}
                        onClick={() => openMessenger()}
                      />
                    </FlexContainer>
                  ),
                },
              ]
            : []),
          {
            align: "center",
            dataIndex: "uploadDate",
            render: (_, { createdAt }) =>
              createdAt && (
                <RegularText>
                  {dayjs(createdAt).format("MMM. DD, YYYY")}
                </RegularText>
              ),
            sorter: (a, b) => {
              if (!isDocument(a) || !isDocument(b)) return 0;
              return moment(a.createdAt).diff(moment(b.createdAt));
            },
            title: (
              <DocumentTableHeader strong>Uploaded Date</DocumentTableHeader>
            ),
          },
          {
            dataIndex: "uploadedBy",
            render: (_, { id, tags, uploadedBy }) =>
              id && (
                <RegularText>
                  {uploadedBy && uploadedBy?.name
                    ? uploadedBy?.name
                    : tags?.includes("manager-upload")
                    ? "Manager"
                    : "Applicant"}
                </RegularText>
              ),
            sorter: (a, b) => {
              if (!isDocument(a) || !isDocument(b)) return 0;
              const aDefaultName = a.tags?.includes("manager-upload")
                ? "Manager"
                : "Applicant";
              const bDefaultName = b.tags?.includes("manager-upload")
                ? "Manager"
                : "Applicant";
              const x = a.uploadedBy?.name ?? aDefaultName;
              const y = b.uploadedBy?.name ?? bDefaultName;
              return x.localeCompare(y);
            },
            title: (
              <DocumentTableHeader strong>Uploaded By</DocumentTableHeader>
            ),
          },
          ...(application?.dealDecisionIsMade ||
          isEmpty(uploadedDocuments) ||
          shouldRedactDocuments
            ? []
            : [
                {
                  dataIndex: "delete",
                  render: (_, { id, type }) =>
                    id &&
                    !["agreement", "contract"].includes(type) &&
                    (inProgressJobs[id] === 1 ? (
                      <Tooltip
                        title="This document is still processing and can’t be deleted yet."
                        theme="dark"
                      >
                        <LoadingSpinnerIcon height={24} width={24} />
                      </Tooltip>
                    ) : inProgressJobs[id] === 2 ? (
                      <Tooltip
                        title="OCR is taking longer than usual and is considered failed"
                        theme="dark"
                      >
                        <DeleteDocumentIcon
                          height={16}
                          onClick={() =>
                            openModalDialog(DeleteDocumentModal, {
                              applicationId: application?.id,
                              documentId: id,
                            })
                          }
                        />
                      </Tooltip>
                    ) : (
                      <DeleteDocumentIcon
                        height={16}
                        onClick={() =>
                          openModalDialog(DeleteDocumentModal, {
                            applicationId: application?.id,
                            documentId: id,
                          })
                        }
                      />
                    )),
                  title: "",
                  width: "5%",
                },
              ]),
        ]}
        dataSource={[...documents]}
        noBorderBottom
        noClickableRows
        pagination={false}
        rowKey={({ id, name, index }) => id ?? `${name}_${index}`}
        size="small"
      />
    </>
  );
};

export default AttachmentsRedesigned;
